'use client';

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../../components/icons';
import { Button } from '../../../components/ui/Button';
import { IconButton } from '../../../components/ui/icon-button';
import { cn } from '../../../lib/utils';
import { useShareMatchResultsEmailMutation } from '../hooks/useShareMatchResultsEmailMutation';

const EMAIL_MAX = 320;
const NAME_MAX = 80;

function isValidEmail(value: string): boolean {
  const v = value.trim();
  if (v.length < 3 || v.length > EMAIL_MAX) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

export type MatchShareEmailDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matchResultsId: string | null;
  /**
   * Same `--theme-*` map as the adopt-quiz / share overlay so the modal matches
   * the selected shelter palette (not `:root` public).
   */
  shelterThemeVars?: Record<string, string> | null;
};

/**
 * Email share modal for the match results share flow.
 *
 * Uses a portaled div overlay instead of the shared native `<dialog>` because
 * stacking a `showModal()` dialog over the full-screen share overlay collapses
 * unpredictably on mobile (footer-only strip / horizontal line).
 */
export function MatchShareEmailDialog({
  open,
  onOpenChange,
  matchResultsId,
  shelterThemeVars = null,
}: MatchShareEmailDialogProps) {
  const { t } = useTranslation('matching');
  const emailMutation = useShareMatchResultsEmailMutation();
  const formId = useId();
  const titleId = useId();
  const emailInputRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (open) return;
    setEmail('');
    setDisplayName('');
    setLocalError(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => {
      emailInputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      onOpenChange(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onOpenChange]);

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      setLocalError(null);
      if (!matchResultsId) {
        setLocalError(t('email.unavailable'));
        return;
      }
      if (!isValidEmail(email)) {
        setLocalError(t('email.invalid'));
        return;
      }
      const name =
        displayName.trim().length > 0
          ? displayName.trim().slice(0, NAME_MAX)
          : undefined;
      emailMutation.mutate(
        {
          matchResultsId,
          recipientEmail: email.trim(),
          recipientName: name,
        },
        {
          onSuccess: (data) => {
            if (data.sent) {
              onOpenChange(false);
            } else {
              setLocalError(t('email.disabled'));
            }
          },
          onError: () => {
            setLocalError(t('email.failed'));
          },
        },
      );
    },
    [email, displayName, matchResultsId, emailMutation, onOpenChange, t],
  );

  const busy = emailMutation.isPending;

  const surfaceThemeStyle = shelterThemeVars
    ? (shelterThemeVars as CSSProperties)
    : undefined;

  if (!open) return null;

  return createPortal(
    <div
      className='fixed inset-0 z-110 flex items-end justify-center sm:items-center sm:p-4'
      role='dialog'
      aria-modal='true'
      aria-labelledby={titleId}
    >
      <button
        type='button'
        className='absolute inset-0 bg-black/50'
        aria-label={t('email.close')}
        onClick={() => onOpenChange(false)}
      />
      <div
        className={cn(
          'relative z-10 flex w-full max-w-lg flex-col overflow-hidden rounded-t-2xl border border-border bg-card text-foreground shadow-xl',
          'max-h-[min(92dvh,40rem)] sm:max-h-[min(90dvh,40rem)] sm:rounded-2xl',
        )}
        data-app-theme={shelterThemeVars ? 'adopt-quiz' : undefined}
        style={surfaceThemeStyle}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div className='relative shrink-0 px-6 pb-2 pt-6 sm:pr-12'>
          <IconButton
            type='button'
            variant='ghost'
            className='absolute right-3 top-3 text-muted-foreground hover:text-foreground [&_svg]:size-5'
            aria-label={t('email.close')}
            onClick={() => onOpenChange(false)}
          >
            <Icon name='close' className='size-5' />
          </IconButton>
          <h2
            id={titleId}
            className='font-heading text-lg font-semibold tracking-tight text-foreground'
          >
            {t('email.heading')}
          </h2>
          <p className='mt-1.5 text-sm text-muted-foreground'>
            {t('email.intro')}
          </p>
        </div>

        <form
          id={formId}
          className='flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain'
          onSubmit={handleSubmit}
        >
          <div className='space-y-4 px-6 py-2'>
            <div>
              <label
                htmlFor={`${formId}-email`}
                className='block text-sm font-medium text-foreground'
              >
                {t('email.email')}
              </label>
              <input
                ref={emailInputRef}
                id={`${formId}-email`}
                type='email'
                name='email'
                autoComplete='email'
                required
                maxLength={EMAIL_MAX}
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                disabled={busy || !matchResultsId}
                className='mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none ring-primary-600 focus-visible:ring-2 disabled:opacity-60'
                placeholder='you@example.com'
              />
            </div>
            <div>
              <label
                htmlFor={`${formId}-name`}
                className='block text-sm font-medium text-foreground'
              >
                {t('email.name')}{' '}
                <span className='font-normal'>{t('email.optional')}</span>
              </label>
              <input
                id={`${formId}-name`}
                type='text'
                name='name'
                autoComplete='given-name'
                maxLength={NAME_MAX}
                value={displayName}
                onChange={(ev) => setDisplayName(ev.target.value)}
                disabled={busy}
                className='mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none ring-primary-600 focus-visible:ring-2 disabled:opacity-60'
                placeholder='Ashley'
              />
            </div>
            <p className='m-1 text-sm text-muted-foreground'>
              {t('email.consent')}
            </p>

            {!matchResultsId ? (
              <p className='text-sm text-amber-800' role='alert'>
                {t('email.missingId')}
              </p>
            ) : null}
            {localError ? (
              <p className='text-sm text-red-600' role='alert'>
                {localError}
              </p>
            ) : null}
          </div>

          <div className='mt-2 flex shrink-0 flex-col-reverse gap-2 border-t border-border bg-card px-6 pt-4 sm:flex-row sm:justify-end pb-[max(1rem,env(safe-area-inset-bottom,0px))]'>
            <Button
              type='button'
              variant='secondary'
              disabled={busy}
              onClick={() => onOpenChange(false)}
            >
              {t('email.cancel')}
            </Button>
            <Button
              type='submit'
              variant='primary'
              disabled={busy || !matchResultsId}
              isLoading={busy}
            >
              {t('email.send')}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
